import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, spring, interpolate } from 'remotion';

// ============================================
// 🚀 超越 100 效果 - 2026 最新动画趋势
// ============================================

// #106 弹性缩放卡片 - React Spring 风格物理动画
export const SpringScaleCard: React.FC<{
  title: string;
  subtitle?: string;
  color?: string;
  delay?: number;
}> = ({ title, subtitle, color = '#6366f1', delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 弹簧物理动画 - 模拟 React Spring
  const scale = spring({
    frame: frame - delay,
    fps,
    config: {
      damping: 8,
      stiffness: 100,
      mass: 0.5,
    },
  });

  const rotation = interpolate(scale, [0, 1], [-15, 0]);
  const opacity = interpolate(scale, [0, 1], [0, 1]);

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
      }}
    >
      <div
        style={{
          width: 400,
          height: 250,
          background: `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
          borderRadius: 24,
          boxShadow: `0 20px 60px ${color}66`,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          transform: `scale(${scale}) rotate(${rotation}deg)`,
          opacity,
          padding: 40,
        }}
      >
        <div
          style={{
            fontSize: 48,
            fontWeight: 'bold',
            color: '#fff',
            marginBottom: subtitle ? 16 : 0,
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: 24,
              color: '#ffffffcc',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </AbsoluteFill>
  );
};

// #107 裁剪路径转场 - clip-path 揭示动画
export const ClipPathTransition: React.FC<{
  text: string;
  direction?: 'left' | 'right' | 'top' | 'bottom' | 'center';
  color?: string;
  delay?: number;
}> = ({ text, direction = 'center', color = '#10b981', delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 根据方向选择不同的 clip-path
  const getClipPath = () => {
    const progress = interpolate(frame - delay, [0, 60], [0, 100], { extrapolateRight: 'clamp' });
    
    switch (direction) {
      case 'left':
        return `inset(0 ${100 - progress}% 0 0)`;
      case 'right':
        return `inset(0 0 0 ${100 - progress}%)`;
      case 'top':
        return `inset(${100 - progress}% 0 0 0)`;
      case 'bottom':
        return `inset(0 0 ${100 - progress}% 0)`;
      case 'center':
      default:
        return `circle(${progress}% at center)`;
    }
  };

  return (
    <AbsoluteFill
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        background: '#000',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          clipPath: getClipPath(),
        }}
      >
        <div
          style={{
            fontSize: 72,
            fontWeight: 'bold',
            color: '#fff',
          }}
        >
          {text}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// #108 手势跟随光点 - 模拟鼠标跟随效果
export const GestureFollowingLight: React.FC<{
  text?: string;
  particleCount?: number;
  delay?: number;
}> = ({ text, particleCount = 30, delay = 0 }) => {
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();

  // 生成跟随粒子
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const t = (frame - delay + i * 3) * 0.05;
    const x = width / 2 + Math.sin(t * 2 + i * 0.5) * (150 + i * 10);
    const y = height / 2 + Math.cos(t * 3 + i * 0.3) * (100 + i * 8);
    const size = 8 + Math.sin(t + i) * 4;
    const hue = (i * 12 + frame * 2) % 360;
    const opacity = interpolate(i, [0, particleCount - 1], [1, 0.3]);

    return { x, y, size, hue, opacity };
  });

  return (
    <AbsoluteFill
      style={{
        background: 'radial-gradient(circle at center, #1e1b4b 0%, #0f0f23 100%)',
      }}
    >
      {/* 光点轨迹 */}
      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: p.x,
            top: p.y,
            width: p.size,
            height: p.size,
            background: `hsl(${p.hue}, 100%, 60%)`,
            borderRadius: '50%',
            opacity: p.opacity,
            boxShadow: `0 0 ${p.size * 2}px hsl(${p.hue}, 100%, 60%)`,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}

      {/* 中心文字 */}
      {text && (
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            fontSize: 64,
            fontWeight: 'bold',
            color: '#fff',
            textShadow: '0 0 40px rgba(99, 102, 241, 0.8)',
          }}
        >
          {text}
        </div>
      )}
    </AbsoluteFill>
  );
};

// #98 弹簧物理动画
export const SpringPhysics: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
}> = ({ text, color, backgroundColor }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    fps,
    frame,
    config: {
      damping: 10,
      stiffness: 200,
      mass: 0.5,
    },
  });

  const rotation = interpolate(
    spring({
      fps,
      frame,
      config: {
        damping: 15,
        stiffness: 100,
        mass: 0.8,
      },
    }),
    [0, 1],
    [-45, 0]
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          fontSize: 150,
          fontWeight: 'bold',
          color,
          transform: `scale(${scale}) rotate(${rotation}deg)`,
        }}
      >
        {text}
      </div>
    </AbsoluteFill>
  );
};

// #99 滚动触发文字
export const ScrollTriggeredText: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
}> = ({ text, color, backgroundColor }) => {
  const frame = useCurrentFrame();
  const letters = text.split('');

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {letters.map((letter, i) => {
          const delay = i * 5;
          const opacity = interpolate(
            frame - delay,
            [0, 10],
            [0, 1],
            { extrapolateRight: 'clamp' }
          );
          const translateY = interpolate(
            frame - delay,
            [0, 15],
            [100, 0],
            { extrapolateRight: 'clamp' }
          );

          return (
            <div
              key={i}
              style={{
                fontSize: 120,
                fontWeight: 'bold',
                color,
                opacity,
                transform: `translateY(${translateY}px)`,
              }}
            >
              {letter === ' ' ? '\u00A0' : letter}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// #100 翻转卡片堆
export const FlipCardStack: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
}> = ({ text, color, backgroundColor }) => {
  const frame = useCurrentFrame();
  const cards = 5;
  const cardWidth = 200;
  const cardHeight = 280;

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
        perspective: 1000,
      }}
    >
      <div style={{ position: 'relative' }}>
        {Array.from({ length: cards }).map((_, i) => {
          const delay = i * 8;
          const rotateY = interpolate(
            frame - delay,
            [0, 30],
            [0, 360],
            { extrapolateRight: 'clamp' }
          );
          const translateZ = i * 10;
          const zIndex = cards - i;

          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                width: cardWidth,
                height: cardHeight,
                backgroundColor: color,
                borderRadius: 20,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                fontSize: 80,
                fontWeight: 'bold',
                color: backgroundColor,
                transform: `translateZ(${translateZ}px) rotateY(${rotateY}deg)`,
                zIndex,
                backfaceVisibility: 'hidden',
              }}
            >
              {text[i % text.length]}
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

// #101 磁性按钮
export const MagneticButton: React.FC<{
  text: string;
  color: string;
  backgroundColor: string;
}> = ({ text, color, backgroundColor }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const scale = spring({
    fps,
    frame,
    config: {
      damping: 12,
      stiffness: 150,
      mass: 0.6,
    },
  });

  const glow = interpolate(
    frame % 60,
    [0, 30, 60],
    [0.3, 1, 0.3]
  );

  const magneticPull = interpolate(
    Math.sin(frame * 0.1),
    [-1, 1],
    [-5, 5]
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          padding: '40px 80px',
          backgroundColor: color,
          borderRadius: 60,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          transform: `scale(${scale}) translateX(${magneticPull}px)`,
          boxShadow: `0 0 ${40 * glow}px ${color}`,
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: backgroundColor,
          }}
        >
          {text}
        </div>
      </div>
    </AbsoluteFill>
  );
};

// #102 3D 视差层效果
export const ParallaxLayers: React.FC<{}> = () => {
  const frame = useCurrentFrame();
  const layers = 5;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f0f23',
        perspective: 1000,
        overflow: 'hidden',
      }}
    >
      {Array.from({ length: layers }).map((_, i) => {
        const depth = (i + 1) / layers;
        const translateZ = interpolate(depth, [0, 1], [-500, 0]);
        const translateX = Math.sin(frame * 0.05 + i * 0.5) * (50 * depth);
        const translateY = Math.cos(frame * 0.05 + i * 0.5) * (30 * depth);
        const opacity = interpolate(depth, [0, 1], [0.2, 1]);

        const hue = interpolate(i, [0, layers - 1], [200, 300]);
        const size = interpolate(depth, [0, 1], [50, 200]);

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: size,
              height: size,
              backgroundColor: `hsla(${hue}, 70%, 50%, ${opacity})`,
              borderRadius: 20,
              left: `calc(50% - ${size / 2}px + ${translateX}px)`,
              top: `calc(50% - ${size / 2}px + ${translateY}px)`,
              transform: `translateZ(${translateZ}px)`,
              boxShadow: `0 0 ${30 * depth}px hsla(${hue}, 70%, 50%, 0.5)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};

// #109 滚动进度条 - 2026 CSS Scroll-Driven 趋势
export const ScrollProgressBar: React.FC<{
  text?: string;
  color?: string;
}> = ({ text = 'Loading...', color = '#6366f1' }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 模拟滚动进度（0-100%）
  const progress = interpolate(
    frame % (fps * 3),
    [0, fps * 3],
    [0, 100],
    { extrapolateRight: 'clamp' }
  );

  // 波浪动画
  const wave = Math.sin(frame * 0.2) * 5;

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0f172a',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div style={{ width: 600, textAlign: 'center' }}>
        {/* 标题文字 */}
        <div
          style={{
            fontSize: 48,
            fontWeight: 'bold',
            color: '#fff',
            marginBottom: 40,
          }}
        >
          {text}
        </div>

        {/* 进度条容器 */}
        <div
          style={{
            width: '100%',
            height: 16,
            backgroundColor: '#1e293b',
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          {/* 进度条填充 */}
          <div
            style={{
              width: `${progress}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${color}, ${color}cc)`,
              borderRadius: 8,
              transform: `translateY(${wave}px)`,
              boxShadow: `0 0 20px ${color}`,
              transition: 'width 0.1s ease-out',
            }}
          />

          {/* 发光点 */}
          <div
            style={{
              position: 'absolute',
              left: `${progress}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: 24,
              height: 24,
              backgroundColor: color,
              borderRadius: '50%',
              boxShadow: `0 0 30px ${color}`,
            }}
          />
        </div>

        {/* 百分比显示 */}
        <div
          style={{
            marginTop: 20,
            fontSize: 32,
            fontWeight: 'bold',
            color: color,
          }}
        >
          {Math.round(progress)}%
        </div>
      </div>
    </AbsoluteFill>
  );
};

// #110 无限文字滚动 - 视差层叠效果
export const InfiniteTextScroll: React.FC<{
  texts?: string[];
}> = ({ texts = ['React', 'Remotion', 'Animation', 'Creative'] }) => {
  const frame = useCurrentFrame();
  const { width } = useVideoConfig();

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#000',
        overflow: 'hidden',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {texts.map((text, layerIndex) => {
        // 不同层不同速度（视差效果）
        const speed = 1 + layerIndex * 0.3;
        const xOffset = (frame * speed * 3 + layerIndex * 200) % (width + 400) - 200;

        // 不同层不同大小和透明度
        const fontSize = 80 - layerIndex * 10;
        const opacity = 1 - layerIndex * 0.15;

        // 不同层不同颜色
        const hue = 200 + layerIndex * 40;

        return (
          <div
            key={layerIndex}
            style={{
              position: 'absolute',
              fontSize,
              fontWeight: 'bold',
              color: `hsla(${hue}, 70%, 60%, ${opacity})`,
              transform: `translateX(${xOffset}px)`,
              whiteSpace: 'nowrap',
              textShadow: `0 0 30px hsla(${hue}, 70%, 60%, 0.5)`,
            }}
          >
            {text}
          </div>
        );
      })}

      {/* 中心高亮文字 */}
      <div
        style={{
          fontSize: 100,
          fontWeight: 'bold',
          color: '#fff',
          textShadow: '0 0 50px rgba(255,255,255,0.5)',
          zIndex: 10,
        }}
      >
        Infinite Scroll
      </div>
    </AbsoluteFill>
  );
};

// #111 粒子连线网络 - 创意编码效果
export const ParticleNetwork: React.FC<{
  particleCount?: number;
}> = ({ particleCount = 40 }) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // 生成粒子
  const particles = Array.from({ length: particleCount }).map((_, i) => {
    const seed = i * 1234.5678;
    const baseX = (Math.sin(seed) * 0.5 + 0.5) * width;
    const baseY = (Math.cos(seed * 2) * 0.5 + 0.5) * height;

    return {
      x: baseX + Math.sin(frame * 0.03 + i) * 80,
      y: baseY + Math.cos(frame * 0.02 + i * 1.5) * 80,
      size: 4 + (i % 4),
      hue: 200 + (i * 7) % 80,
    };
  });

  // 计算连线（距离 < 150px 的粒子）
  const connections: Array<{ x1: number; y1: number; x2: number; y2: number; opacity: number }> = [];
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      const dx = particles[i].x - particles[j].x;
      const dy = particles[i].y - particles[j].y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < 150) {
        connections.push({
          x1: particles[i].x,
          y1: particles[i].y,
          x2: particles[j].x,
          y2: particles[j].y,
          opacity: 1 - distance / 150,
        });
      }
    }
  }

  return (
    <AbsoluteFill style={{ backgroundColor: '#0a0a1a' }}>
      {/* 连线 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {connections.map((conn, i) => (
          <line
            key={i}
            x1={conn.x1}
            y1={conn.y1}
            x2={conn.x2}
            y2={conn.y2}
            stroke={`rgba(99, 102, 241, ${conn.opacity * 0.4})`}
            strokeWidth={1}
          />
        ))}
      </svg>

      {/* 粒子 */}
      {particles.map((particle, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: particle.x,
            top: particle.y,
            width: particle.size,
            height: particle.size,
            backgroundColor: `hsl(${particle.hue}, 70%, 60%)`,
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            boxShadow: `0 0 ${particle.size * 3}px hsl(${particle.hue}, 70%, 60%)`,
          }}
        />
      ))}

      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          fontSize: 80,
          fontWeight: 'bold',
          color: '#fff',
          textShadow: '0 0 30px rgba(99, 102, 241, 0.8)',
          zIndex: 10,
        }}
      >
        Network
      </div>
    </AbsoluteFill>
  );
};
